import React from 'react';
import { useNavigate } from 'react-router';

import { Tooltip } from 'antd';

import styled from 'styled-components';

import { getAge } from '@libs/NwMoment';
import { showDateNoTimeAsDateOnly } from '@libs/NwMoment';

import { getModel, modelFullName } from '@hooks/API/Contact/UseContactsList';

import ModelDetailsHeaderAgencies from './CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';

import ContactDepartments from '@components/ContactDepartments/ContactDepartments';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import CountryLabel from '@components/Gui/CountryLabel';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwIcon, { NwGenderIcon } from '@components/Gui/NwIcon';
import { ModelCover } from '@components/ModelCover';
import ModelInTown from '@components/ModelInTown/ModelInTown';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const HeaderWrapper = styled.div`
    padding-bottom: 1rem;
    padding-right: 3rem;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    margin-bottom: 1rem;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 2rem;
    position: relative;

    .model-trigger-fullview {
        position: absolute;
        top: 0;
        right: 0;
        font-size: ${(props) => props.theme.FontSizeXLg};
    }

    .model-cover-wrapper {
        padding-top: 0.5rem;
    }

    .model-details-wrapper {
        h1 {
            font-size: 1.3rem;
            font-weight: bold;
            margin-bottom: 0.15rem;
        }
        p {
            margin: 0;
        }
        .header-line {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 2px;

            svg,
            span {
                margin-top: 0;
            }
        }
    }
`;

const ModelHeader = ({ modelID }) => {
    const contact = getModel(modelID);
    const fullName = modelFullName(contact);
    const birthDate = contact.B;
    const nationalityCode = contact.NAT;
    const gender = contact.G;

    const navigate = useNavigate();

    const getBirthdayContent = () => (
        <>
            {getAge(birthDate)} y.o.&nbsp;&nbsp;
            <NwIcon icon={byPrefixAndName.fasl['birthday-cake']} />
            &nbsp;&nbsp;{showDateNoTimeAsDateOnly(birthDate)}
        </>
    );

    const goToModelPage = () => {
        navigate(`/model/${modelID}`);
    };

    const renderContactDetailsHeaderAnagraphical = () => {
        return contact.ContactType === 'Customer' ? (
            <NwIcon icon={byPrefixAndName.fasl['id-card']} />
        ) : (
            <>
                <NwGenderIcon gender={`${gender}`} />
                &nbsp;&nbsp;
                {birthDate ? getBirthdayContent() : `birthday: n.d.`}
                &nbsp;&nbsp;&nbsp;
                {nationalityCode && <CountryLabel usecode code={nationalityCode} flag />}
            </>
        );
    };

    return (
        <HeaderWrapper>
            <div className="model-trigger-fullview">
                <Tooltip placement="left" title="Leave the search and go to model page">
                    <NwIcon pointer icon={byPrefixAndName.fass['expand-wide']} onClick={goToModelPage} />
                </Tooltip>
            </div>
            <div className="model-cover-wrapper">
                <ModelCover maxWidth={70} model={contact} />
            </div>
            <div className="model-details-wrapper">
                <h1>{fullName}</h1>
                <div className="header-line">
                    <ModelInTown modelID={contact.ID} modelInTownSchedule={contact.ITS} readOnly />
                    <NwDividerVertical />
                    <ModelDetailsHeaderAgencies model={contact} />
                </div>
                <div className="header-line">
                    <div className="header-body-boxes header-body-boxes-hoverable">
                        <ContactUsers users={contact.U} contactID={contact.ID} />
                    </div>
                    <NwDividerVertical />
                    <div className="header-body-boxes">
                        <ContactDepartments departments={contact.DEPS} contactID={contact.ID} />
                    </div>
                </div>
                <div className="header-line">{renderContactDetailsHeaderAnagraphical()}</div>
            </div>
        </HeaderWrapper>
    );
};

export default ModelHeader;
